import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classes from './ProductCta.module.scss';

// Fixed navbar covers the top of the viewport, a CTA hidden under it does not count as visible
const NAVBAR_OFFSET = 64;
const WATCHED_SELECTOR = '[data-product-cta], .footer';

// Bottom bar that slides in whenever no other product CTA is on screen,
// so the action stays reachable on long pages. It steps aside for the footer.
const ProductCtaBar = ({ title, children }) => {
  const [visible, setVisible] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    if (
      typeof IntersectionObserver === 'undefined' ||
      typeof MutationObserver === 'undefined'
    ) {
      return undefined;
    }

    const observed = new Set();
    const inView = new Set();
    let measured = false;
    let frame = null;

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            inView.add(entry.target);
          } else {
            inView.delete(entry.target);
          }
        });
        measured = true;
        setVisible(inView.size === 0);
      },
      { rootMargin: `-${NAVBAR_OFFSET}px 0px 0px 0px` }
    );

    const sync = () => {
      frame = null;

      const targets = new Set(
        Array.from(document.querySelectorAll(WATCHED_SELECTOR)).filter(
          (element) => !barRef.current || !barRef.current.contains(element)
        )
      );

      observed.forEach((element) => {
        if (!targets.has(element)) {
          intersectionObserver.unobserve(element);
          observed.delete(element);
          inView.delete(element);
        }
      });

      targets.forEach((element) => {
        if (!observed.has(element)) {
          intersectionObserver.observe(element);
          observed.add(element);
        }
      });

      if (measured) {
        setVisible(inView.size === 0);
      }
    };

    // Sub-pages swap content under the same shell, so the set of CTAs changes over time
    const mutationObserver = new MutationObserver(() => {
      if (frame === null) {
        frame = window.requestAnimationFrame(sync);
      }
    });

    sync();
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      mutationObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, []);

  return ReactDOM.createPortal(
    <div
      ref={barRef}
      className={`${classes.bar} ${visible ? classes.barVisible : ''}`}
      aria-hidden={!visible}>
      <div className={classes.barInner}>
        {title && <span className={classes.barTitle}>{title}</span>}
        <div className={classes.barActions}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default ProductCtaBar;
